'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { Beer, ArrowRight, Wallet, CheckCircle, Store, Zap } from 'lucide-react';

export default function LandingPage() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stagger = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-amber-500/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-amber-600/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-card mx-4 mt-4 border-none !bg-surface-900/50 flex items-center justify-between px-6 py-4">
        <Link href="/" className="flex items-center gap-2 text-2xl font-display font-bold">
          <span>🍺</span>
          <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">MoreBeer</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <Link href="#comment-ca-marche" className="hover:text-amber-400 transition-colors">Fonctionnement</Link>
          <Link href="/pricing" className="hover:text-amber-400 transition-colors">Tarifs</Link>
          <Link href="#bars" className="hover:text-amber-400 transition-colors">Bars</Link>
        </nav>
        <div className="flex items-center gap-4">
          <Link href="/login" className="text-sm font-medium hover:text-amber-400 transition-colors">Connexion</Link>
          <Link href="/register" className="bg-amber-500 hover:bg-amber-400 text-surface-950 font-bold px-4 py-2 rounded-full transition-all hover:scale-105 active:scale-95 shadow-[0_0_15px_rgba(245,158,11,0.4)]">
            S'abonner
          </Link>
        </div>
      </header>

      <main className="flex-1 mt-32">
        {/* Hero Section */}
        <section className="px-6 py-20 max-w-6xl mx-auto text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.h1 variants={fadeInUp} className="text-5xl md:text-7xl font-display font-bold leading-tight mb-6">
              Bois au tarif Happy Hour.<br/>
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 bg-clip-text text-transparent">Tout le temps.</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg md:text-xl text-surface-200 mb-10 max-w-2xl mx-auto">
              Abonne-toi à MoreBeer, paie tes verres moins chers dans tous nos bars partenaires, et accumule du cashback automatique.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/pricing" className="bg-amber-500 hover:bg-amber-400 text-surface-950 text-lg font-bold px-8 py-4 rounded-full transition-all flex items-center gap-2 hover:gap-4 amber-glow w-full sm:w-auto justify-center">
                Découvrir les offres <ArrowRight size={20} />
              </Link>
              <Link href="/register" className="glass-card px-8 py-4 rounded-full font-medium hover:bg-surface-800 transition-colors w-full sm:w-auto justify-center flex items-center">
                C'est gratuit pendant 7 jours
              </Link>
            </motion.div>
          </motion.div>
        </section>

        {/* Comment ça marche */}
        <section id="comment-ca-marche" className="px-6 py-24 max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-4">Comment ça marche ?</h2>
            <p className="text-surface-300">Simple, rapide, automatique.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Zap, title: "1. Abonne-toi", desc: "Choisis l'abonnement qui te correspond le mieux." },
              { icon: Store, title: "2. Connecte ta carte", desc: "Liaison bancaire sécurisée pour tracker tes consos automatiquement." },
              { icon: Wallet, title: "3. Profite du cashback", desc: "Le cashback est crédité directement sur ton compte." }
            ].map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }} className="glass-card p-8 text-center flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-6 text-amber-500">
                  <step.icon size={32} />
                </div>
                <h3 className="text-xl font-bold font-display mb-2">{step.title}</h3>
                <p className="text-surface-300">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Stats */}
        <section className="bg-surface-900 py-16 border-y border-glass-border">
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <span className="text-4xl md:text-5xl font-display font-bold text-amber-500 mb-2">10 000+</span>
              <span className="text-surface-300 font-medium">Utilisateurs actifs</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-4xl md:text-5xl font-display font-bold text-amber-500 mb-2">500+</span>
              <span className="text-surface-300 font-medium">Bars partenaires</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-4xl md:text-5xl font-display font-bold text-cash mb-2">2M€</span>
              <span className="text-surface-300 font-medium">Économisés</span>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="px-6 py-24 max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="glass-card p-10 flex flex-col justify-center relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-bl-full transition-transform group-hover:scale-110" />
              <CheckCircle className="text-amber-500 mb-4" size={40} />
              <h3 className="text-2xl font-display font-bold mb-4">Happy Hour H24</h3>
              <p className="text-surface-300">Plus besoin de courir pour arriver avant 20h. Ton tarif est bloqué à l'heure la plus heureuse.</p>
            </div>
            <div className="glass-card p-10 flex flex-col justify-center relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cash/10 rounded-bl-full transition-transform group-hover:scale-110" />
              <Wallet className="text-cash mb-4" size={40} />
              <h3 className="text-2xl font-display font-bold mb-4">Cashback Automatique</h3>
              <p className="text-surface-300">Paiement avec ta carte classique. On détecte et on te crédite l'argent. Zéro prise de tête.</p>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="px-6 py-20">
          <div className="max-w-4xl mx-auto glass-card p-12 text-center rounded-3xl relative overflow-hidden amber-glow">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-transparent pointer-events-none" />
            <h2 className="text-4xl font-display font-bold mb-6">Rejoins MoreBeer aujourd'hui</h2>
            <p className="text-xl text-surface-200 mb-8 max-w-xl mx-auto">Commence à économiser sur tes soirées dès ce soir.</p>
            <Link href="/pricing" className="inline-block bg-amber-500 hover:bg-amber-400 text-surface-950 text-lg font-bold px-10 py-4 rounded-full transition-all hover:scale-105 active:scale-95">
              Voir nos abonnements
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-glass-border py-8 mt-12 bg-surface-900">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xl font-display font-bold">
            <span>🍺</span>
            <span>MoreBeer</span>
          </div>
          <div className="text-surface-400 text-sm">© 2026 MoreBeer. Tous droits réservés. L'abus d'alcool est dangereux pour la santé.</div>
          <div className="flex gap-4 text-sm font-medium">
            <Link href="#" className="hover:text-amber-400">Mentions Légales</Link>
            <Link href="#" className="hover:text-amber-400">CGV</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
